// The Invoices tab on the partner editor — the monthly two-way settlement
// with one partner, from ops' side of the desk.
//
// ===========================================================================
// WHAT AN OPERATOR IS DOING HERE
//
//   1. picking a month and pressing Generate, which cuts one DRAFT per
//      currency out of that month's confirmed bookings;
//   2. reading the lines to check the figures;
//   3. issuing it (the document is now SENT), then marking it paid.
//
// Both sides are always on screen, never just the net. "Partner owes" is what
// the partner collected on DriveCars' behalf — its own merchant-of-record
// bookings, where its PSP took the card and it is holding our fee or markup.
// "DriveCars owes" is the partner's shares on the bookings we charged for.
// The net is the difference, and its SIGN decides who pays whom — which is
// why the direction is spelled out in words beside it rather than left for an
// operator to read off a minus.
//
// EVERY MONTH IS PER CURRENCY, and two are never added. A partner trading in
// EUR and MAD has two invoices for September, each settled on its own.
//
// WHAT THE STATE MACHINE REFUSES matters more than what it allows, so every
// refusal reaching this screen is turned into a sentence:
//
//   invoice_immutable       EVERY currency this month holds has been sent —
//                           there is nothing left to recut. A month with one
//                           issued currency and one still open is NOT refused:
//                           the open one is rebuilt and the sent one is left
//                           exactly as the partner received it. Bookings that
//                           arrive after a currency has been sent roll forward
//                           into the next month generated.
//   invalid_transition      e.g. paying a document nobody sent
//   first_party_no_invoice  DriveCars does not bill itself
//
// The panel is exported on its own so its test can render it without the
// whole editor around it, the same way PartnerTeamPanel is.
// ===========================================================================

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ToolBtn } from '@drivecars/admin-ui';
import {
  fetchPartnerInvoice,
  fetchPartnerInvoices,
  generatePartnerInvoice,
  transitionPartnerInvoice,
  type AdminPartnerInvoice,
  type AdminPartnerInvoiceLineKind,
  type AdminPartnerInvoiceStatus,
} from '@/lib/admin-api';
import { ApiError } from '@/lib/api';

/** Money WITH its currency, always — this screen never converts and never
 *  adds two currencies. An unknown code must not blank a cell. */
function money(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat('en', {
      style: 'currency',
      currency,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${currency} ${amount.toFixed(2)}`;
  }
}

/** A cycle index (`year × 12 + month − 1`) as the `<input type="month">` value
 *  it corresponds to. The two are the same calendar, in UTC. */
export function cycleToMonthInput(cycleIndex: number): string {
  const year = Math.floor(cycleIndex / 12);
  const month = cycleIndex % 12; // 0-based
  return `${year}-${String(month + 1).padStart(2, '0')}`;
}

/** The inverse. Returns null for anything that is not YYYY-MM, so a half-typed
 *  value never becomes a request for the year 20. */
export function monthInputToCycle(value: string): number | null {
  const m = /^(\d{4})-(\d{2})$/.exec(value);
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  if (month < 1 || month > 12) return null;
  return year * 12 + (month - 1);
}

/** The last day INSIDE the period, for display. `periodEnd` is the first day
 *  of the NEXT month — honest for arithmetic, wrong to show a person, who
 *  would read September as running into October. */
function lastDayOf(periodEnd: string): string {
  const end = new Date(`${periodEnd}T00:00:00Z`);
  if (Number.isNaN(end.getTime())) return periodEnd;
  return new Date(end.getTime() - 86_400_000).toISOString().slice(0, 10);
}

const STATUS_LABEL: Record<AdminPartnerInvoiceStatus, string> = {
  draft: 'Draft',
  issued: 'Issued',
  paid: 'Paid',
  void: 'Void',
};

const KIND_LABEL: Record<AdminPartnerInvoiceLineKind, string> = {
  fee_share: 'Partner’s share of the booking fee',
  markup_share: 'Partner’s share of the markup',
  supplier_commission_share: 'Partner’s share of the supplier commission',
  drivecars_fee: 'DriveCars’ booking fee',
  drivecars_markup: 'DriveCars’ markup',
  adjustment: 'Adjustment',
};

/** An expected refusal as a sentence. Anything unexpected keeps the server's
 *  own wording rather than being flattened into "request failed". */
function refusalMessage(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: string; message?: string } | undefined;
    switch (body?.error) {
      case 'invoice_immutable':
        return (
          body.message ??
          'Every currency in this period has already been sent. A booking that arrived since will roll into the next month generated.'
        );
      case 'invalid_transition':
        return body.message ?? 'That is not a move this invoice can make from where it is.';
      case 'first_party_no_invoice':
        return 'This is a first-party DriveCars record — the platform does not invoice itself.';
      case 'not_found':
        return 'That invoice no longer exists.';
      default:
        break;
    }
  }
  return (err as Error)?.message ?? 'Something went wrong.';
}

const notice = 'rounded-lg border px-3 py-2 text-[12.5px]';
const alertClass = `${notice} border-[#fecaca] bg-[#fff1f2] text-[#9f1239]`;
const okClass = `${notice} border-[#bbf7d0] bg-[#f0fdf4] text-[#166534]`;

interface PartnerInvoicesPanelProps {
  partnerId: number;
}

export function PartnerInvoicesPanel({ partnerId }: PartnerInvoicesPanelProps) {
  const queryClient = useQueryClient();
  const queryKey = ['admin', 'partner-invoices', partnerId];

  const { data, isLoading, error } = useQuery({
    queryKey,
    queryFn: ({ signal }) => fetchPartnerInvoices(partnerId, signal),
  });
  const invoices = data?.invoices ?? [];

  // The month picker defaults to the PREVIOUS month once the server has told
  // us which one is current: the month just ended is the one ops settles.
  const [month, setMonth] = useState('');
  const effectiveMonth =
    month || (data ? cycleToMonthInput(Math.max(0, data.currentCycleIndex - 1)) : '');
  const cycleIndex = monthInputToCycle(effectiveMonth);

  const [generated, setGenerated] = useState<number | null>(null);
  const [openId, setOpenId] = useState<number | null>(null);

  const generateMut = useMutation({
    mutationFn: () => generatePartnerInvoice(partnerId, cycleIndex!),
    onSuccess: (res) => {
      void queryClient.invalidateQueries({ queryKey });
      setGenerated(res.invoices.length);
    },
  });

  const transitionMut = useMutation({
    mutationFn: ({ id, action }: { id: number; action: 'issue' | 'mark-paid' | 'void' }) =>
      transitionPartnerInvoice(id, action),
    onSuccess: (_res, vars) => {
      void queryClient.invalidateQueries({ queryKey });
      void queryClient.invalidateQueries({
        queryKey: ['admin', 'partner-invoice', vars.id],
      });
    },
  });

  return (
    <div className="space-y-4">
      <p className="text-[12px] text-[color:var(--bo-mute)]">
        The monthly settlement with this partner — one invoice per currency, cut from the month’s
        confirmed bookings. A draft can be regenerated as often as you like; once it is issued it is
        the document the partner has, and a later booking falls into the next month instead.
      </p>

      <form
        className="rounded-xl border border-[color:var(--bo-line)] p-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (cycleIndex == null || generateMut.isPending) return;
          setGenerated(null);
          generateMut.mutate();
        }}
      >
        <p className="mb-3 text-[12px] font-semibold uppercase tracking-wide text-[color:var(--bo-mute)]">
          Generate a month
        </p>
        <div className="flex flex-wrap items-end gap-2">
          <label className="block">
            <span className="text-[11.5px] font-semibold uppercase tracking-wide text-[color:var(--bo-mute)]">
              Month
            </span>
            <input
              type="month"
              aria-label="Month"
              value={effectiveMonth}
              onChange={(e) => setMonth(e.target.value)}
              className="mt-1 h-10 rounded-lg border border-[color:var(--bo-line)] px-3 text-[13.5px] outline-none focus:border-[color:var(--bo-accent)]"
            />
          </label>
          <button
            type="submit"
            disabled={cycleIndex == null || generateMut.isPending}
            className="inline-flex h-10 shrink-0 items-center rounded-lg bg-[color:var(--bo-ink)] px-3 text-[12px] font-semibold text-white transition hover:bg-black disabled:cursor-not-allowed disabled:opacity-60"
          >
            {generateMut.isPending ? 'Generating…' : 'Generate'}
          </button>
        </div>
        <p className="mt-2 text-[11.5px] text-[color:var(--bo-mute)]">
          Calendar months in UTC, the same cycle the crew statements use.
        </p>
      </form>

      {generateMut.error ? (
        <div role="alert" className={alertClass}>
          {refusalMessage(generateMut.error)}
        </div>
      ) : null}

      {generated != null ? (
        <div role="status" className={okClass}>
          {generated === 0
            ? 'No billable bookings in that month — nothing to invoice.'
            : `Generated ${generated} invoice${generated === 1 ? '' : 's'} for that month, one per currency.`}
        </div>
      ) : null}

      {transitionMut.error ? (
        <div role="alert" className={alertClass}>
          {refusalMessage(transitionMut.error)}
        </div>
      ) : null}

      {error ? (
        <div role="alert" className={alertClass}>
          Couldn&apos;t load the invoices: {(error as Error).message}
        </div>
      ) : null}

      <div className="overflow-x-auto rounded-xl border border-[color:var(--bo-line)]">
        <table className="w-full text-start text-[12.5px]">
          <thead className="bg-[color:var(--bo-bg)] text-[11px] uppercase tracking-wide text-[color:var(--bo-mute)]">
            <tr>
              <th className="px-3 py-2 text-start font-semibold">Period</th>
              <th className="px-3 py-2 text-start font-semibold">Currency</th>
              <th className="px-3 py-2 text-end font-semibold">Partner owes</th>
              <th className="px-3 py-2 text-end font-semibold">DriveCars owes</th>
              <th className="px-3 py-2 text-end font-semibold">Net</th>
              <th className="px-3 py-2 text-start font-semibold">Status</th>
              <th className="px-3 py-2 text-end font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={7} className="px-3 py-4 text-center text-[color:var(--bo-mute)]">
                  Loading…
                </td>
              </tr>
            ) : invoices.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-3 py-4 text-center text-[color:var(--bo-mute)]">
                  No invoices for this partner yet.
                </td>
              </tr>
            ) : (
              invoices.map((invoice) => (
                <InvoiceRows
                  key={invoice.id}
                  invoice={invoice}
                  open={openId === invoice.id}
                  onToggle={() => setOpenId(openId === invoice.id ? null : invoice.id)}
                  onTransition={(action) => {
                    transitionMut.mutate({ id: invoice.id, action });
                  }}
                  busy={transitionMut.isPending}
                />
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function InvoiceRows({
  invoice,
  open,
  onToggle,
  onTransition,
  busy,
}: {
  invoice: AdminPartnerInvoice;
  open: boolean;
  onToggle: () => void;
  onTransition: (action: 'issue' | 'mark-paid' | 'void') => void;
  busy: boolean;
}) {
  // The direction in words. A signed number alone makes an operator work out
  // who pays whom from a minus sign.
  const direction =
    invoice.net > 0
      ? 'partner pays DriveCars'
      : invoice.net < 0
        ? 'DriveCars pays partner'
        : 'nothing due';

  return (
    <>
      <tr className="border-t border-[color:var(--bo-line)]">
        <td className="px-3 py-2">
          {invoice.periodStart} – {lastDayOf(invoice.periodEnd)}
          <span className="ms-2 text-[11px] text-[color:var(--bo-mute)]">
            {invoice.bookingCount} booking{invoice.bookingCount === 1 ? '' : 's'}
          </span>
        </td>
        <td className="px-3 py-2">{invoice.currency}</td>
        <td className="px-3 py-2 text-end tabular-nums">
          {money(invoice.partnerOwes, invoice.currency)}
        </td>
        <td className="px-3 py-2 text-end tabular-nums">
          {money(invoice.drivecarsOwes, invoice.currency)}
        </td>
        <td className="px-3 py-2 text-end tabular-nums">
          {money(invoice.net, invoice.currency)}
          <span className="ms-2 text-[11px] text-[color:var(--bo-mute)]">{direction}</span>
        </td>
        <td className="px-3 py-2">{STATUS_LABEL[invoice.status]}</td>
        <td className="px-3 py-2">
          <div className="flex flex-wrap items-center justify-end gap-1.5">
            <ToolBtn onClick={onToggle} aria-expanded={open}>
              {open ? 'Hide lines' : 'Lines'}
            </ToolBtn>
            {/* Only the moves the state machine actually allows are offered —
                a button the API will always refuse is a button that teaches an
                operator to ignore refusals. */}
            {invoice.status === 'draft' ? (
              <ToolBtn disabled={busy} onClick={() => onTransition('issue')}>
                Issue
              </ToolBtn>
            ) : null}
            {invoice.status === 'issued' ? (
              <ToolBtn disabled={busy} onClick={() => onTransition('mark-paid')}>
                Mark paid
              </ToolBtn>
            ) : null}
            {invoice.status === 'draft' || invoice.status === 'issued' ? (
              <ToolBtn disabled={busy} onClick={() => onTransition('void')}>
                Void
              </ToolBtn>
            ) : null}
          </div>
        </td>
      </tr>
      {open ? (
        <tr>
          <td colSpan={7} className="bg-[color:var(--bo-bg)]/50 px-3 py-3">
            <InvoiceLines invoiceId={invoice.id} />
          </td>
        </tr>
      ) : null}
    </>
  );
}

function InvoiceLines({ invoiceId }: { invoiceId: number }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'partner-invoice', invoiceId],
    queryFn: ({ signal }) => fetchPartnerInvoice(invoiceId, signal),
  });

  if (error) {
    return (
      <div role="alert" className={alertClass}>
        {refusalMessage(error)}
      </div>
    );
  }
  if (isLoading || !data) {
    return <p className="text-[12px] text-[color:var(--bo-mute)]">Loading the lines…</p>;
  }

  const { lines, currency } = data.invoice;
  if (lines.length === 0) {
    return <p className="text-[12px] text-[color:var(--bo-mute)]">This invoice has no lines.</p>;
  }

  return (
    <table className="w-full text-start text-[12px]">
      <caption className="sr-only">Every booking that contributed to this invoice.</caption>
      <thead className="text-[10.5px] uppercase tracking-wide text-[color:var(--bo-mute)]">
        <tr>
          <th className="px-2 py-1 text-start font-semibold">Booking</th>
          <th className="px-2 py-1 text-start font-semibold">Model</th>
          <th className="px-2 py-1 text-start font-semibold">Line</th>
          <th className="px-2 py-1 text-end font-semibold">Amount</th>
        </tr>
      </thead>
      <tbody>
        {lines.map((line) => (
          <tr key={line.id} className="border-t border-[color:var(--bo-line)]">
            <td className="px-2 py-1">{line.reference ?? '—'}</td>
            <td className="px-2 py-1">{line.model ?? '—'}</td>
            <td className="px-2 py-1">
              {KIND_LABEL[line.kind]}
              <span className="ms-2 text-[10.5px] text-[color:var(--bo-mute)]">
                {line.side === 'partner_owes' ? 'partner owes' : 'DriveCars owes'}
              </span>
            </td>
            <td className="px-2 py-1 text-end tabular-nums">
              {money(line.amount, line.currency || currency)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
